import { API_BASE, AUTH_BASE, FEEDBACK_FORM_URL } from '../config';
/**
 * Dashboard Component
 * Modern interface for users to browse and manage listings with sidebar navigation and category filtering
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ListingForm from './ListingForm';
import ListingCard from './ListingCard';
import socketService from '../services/socketService';
import ChatModal from './ChatModal';
import chatService from '../services/chatService';
import ChatDrawer from './ChatDrawer';
import UserProfile from './UserProfile';
import { useChat } from '../context/ChatContext';
import ProfilePicture from './ProfilePicture';
import { PinInput, PinInputField } from '@chakra-ui/react';
import LoadingSpinner from './LoadingSpinner';
import ReactDOM from 'react-dom';
import { useToast } from '../context/ToastContext';
import ConfirmModal from './ui/ConfirmModal';
import TransactionHistory from './TransactionHistory';
import useIsMobile from '../hooks/useIsMobile';

function Dashboard({ setIsAuthenticated, setUser, user }) {
  const navigate = useNavigate();
  const toast = useToast();
  const isMobile = useIsMobile();
  const [confirmModal, setConfirmModal] = useState({ open: false, title: '', message: '', onConfirm: null, danger: false });
  const [allListings, setAllListings] = useState([]);
  const [filteredListings, setFilteredListings] = useState([]);
  const [userListings, setUserListings] = useState({ current: [], completed: [] });
  const [showListingForm, setShowListingForm] = useState(false);
  const [editingListing, setEditingListing] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('browse'); // Always browse now with new navigation
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState(''); // top-bar global search
  const [searchResults, setSearchResults] = useState(null); // backend search results (null = not searching)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [chatRoom, setChatRoom] = useState(null); //chatRoom is the chat room
  const [chatModalOpen, setChatModalOpen] = useState(false); //chatModalOpen is the state to open the chat modal
  const { openRoom, rooms, addOrUpdateRoom } = useChat(); //openRoom is the function to open the chat room, rooms is the list of chat rooms, addOrUpdateRoom is the function to add or update a room
  const [drawerOpen, setDrawerOpen] = useState(false); //drawerOpen is the state to open the chat drawer
  const [profileModalOpen, setProfileModalOpen] = useState(false); //profileModalOpen is the state to open the profile modal
  const [sellerProfileModalOpen, setSellerProfileModalOpen] = useState(false); //sellerProfileModalOpen is the state to open seller profile modal
  const [selectedSellerId, setSelectedSellerId] = useState(null); //selectedSellerId is the seller whose profile to show
  // Stripe onboarding state removed in Phase 1 (ENABLE_STRIPE=false)

  // --- Transaction states ---
  const [activePurchases, setActivePurchases] = useState([]); // purchases where user is buyer and status = paid
  const [activeSales, setActiveSales] = useState([]); // purchases where user is seller and status = paid
  const [holds, setHolds] = useState([]); // admin-only holds
  const [completedTransactions, setCompletedTransactions] = useState([]);
  const [pinInputs, setPinInputs] = useState({}); // Store PIN inputs for each sale
  const [transactionHistory, setTransactionHistory] = useState([]); // Transaction history
  // showTransactionHistory state moved into <TransactionHistory> component
  const [buyingRequests, setBuyingRequests] = useState([]); // All buying requests (incoming + outgoing)
  const [savedListings, setSavedListings] = useState([]); // bookmarked listings (still-available only)
  const [deepLinkedListing, setDeepLinkedListing] = useState(null); // listing opened via ?listing=<id>
  // snackbar replaced by ToastContext — no local state needed
  const [txModal, setTxModal] = useState({ open:false, message:'', type:'success' });

  // ---- UI state: dismissed completed items ----
  // Keep track of which completed direct or stripe transactions a user has dismissed so they no longer
  // appear in the main dashboard sections (they will still live in the permanent Transaction History)
  const [dismissedDirectCompletedIds, setDismissedDirectCompletedIds] = useState([]);
  const [dismissedStripeCompletedIds, setDismissedStripeCompletedIds] = useState([]);

  // Inline success banners that appear briefly under each transactions section
  const [recentStripeCompletions, setRecentStripeCompletions] = useState([]); // {id, role, title}
  const [recentDirectCompletions, setRecentDirectCompletions] = useState([]);

  // Collapsible completed sections
  const [collapsedStripeCompleted, setCollapsedStripeCompleted] = useState(false);
  const [collapsedDirectCompleted, setCollapsedDirectCompleted] = useState(false);

  const categories = [
    { id: 'all', name: 'All Listings' },
    { id: 'textbooks', name: 'Textbooks' },
    { id: 'electronics', name: 'Electronics' },
    { id: 'clothing', name: 'Clothing' },
    { id: 'dorm-supplies', name: 'Dorm Supplies' },
    { id: 'creative-goods', name: 'Others' },
    { id: 'free', name: 'Free Items' }
  ];

  // Split paid vs free for clearer sidebar rendering
  const paidCategories = categories.filter(cat => cat.id !== 'free' && cat.id !== 'all');
  const freeCategory = categories.find(cat => cat.id === 'free');
  const selectedCategoryMeta = categories.find(cat => cat.id === selectedCategory);

  useEffect(() => { //useEffect is a hook that is used to fetch the listings and user listings
    if (!user?.email) {  //? is used to check if the user is logged in
      navigate('/login'); //navigate to the login page
      return;
    }

    fetchAllListings(); //fetchAllListings is the function to fetch all the listings, these are listings from other users
    fetchUserListings(); //fetchUserListings is the function to fetch the user listings
  }, [user?.email, navigate]);

  useEffect(() => {
    filterListings(); //filterListings is the function to filter the listings
  }, [allListings, selectedCategory, userListings, searchQuery, searchResults]);

  // Debounced global search → backend (searches title + description across all categories)
  useEffect(() => {
    const q = searchQuery.trim();
    if (!q) { setSearchResults(null); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE}/listings?q=${encodeURIComponent(q)}`, { credentials: 'include' });
        if (res.ok) setSearchResults(await res.json());
      } catch (err) {
        console.error('Search error:', err);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    // Stripe activePurchases/activeSales fetch removed in Phase 1 (ENABLE_STRIPE=false)

    const fetchTransactionHistory = async () => {
      if (!user) return;
      try {
        const res = await fetch(`${API_BASE}/purchases/history`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          console.log('📜 Transaction history response:', data);
          setTransactionHistory(data);

          // Add recent Stripe completions so they show up even after reload
          const stripeCompletions = data
            .filter(t => t.paymentMethod === 'stripe')
            .map(t => ({
              role: t.role,
              listing: {
                _id: t.id, // Use purchase/transaction id as fallback key
                title: t.listingTitle,
                itemId: t.itemId
              }
            }));
          setCompletedTransactions(prev => {
            const existingIds = new Set(prev.map(c => c.listing._id));
            const merged = [...prev];
            stripeCompletions.forEach(c => {
              if (!existingIds.has(c.listing._id)) merged.push(c);
            });
            return merged;
          });
        }
      } catch (err) {
        console.error('Fetch transaction history error:', err);
      }
    };

    fetchTransactionHistory();
    fetchBuyingRequests();
    fetchSavedListings();
  }, [user]);

  const fetchAllListings = async () => { //fetchAllListings is the function to fetch all the listings
    try {
      const response = await fetch(`${API_BASE}/listings`, {
        credentials: 'include'
      });
      const data = await response.json();
      
      // Only show unclaimed listings
      const availableListings = data.filter(listing => !listing.isClaimed); //filter out the listings that are claimed
      setAllListings(availableListings); //set the listings to the state
    } catch (error) {
      console.error('Error fetching all listings:', error); //log the error
      setAllListings([]); //set the listings to an empty array
    }
  };

  const fetchUserListings = async () => { //fetchUserListings is the function to fetch the user listings
    try {
      const response = await fetch(
        `${API_BASE}/listings?email=${encodeURIComponent(user.email)}`,
        { credentials: 'include' }
      );
      const data = await response.json();
      
      setUserListings({ //set the user listings to the state
        current: data.filter(listing => !listing.isClaimed), //filter out the listings that are claimed
        completed: data.filter(listing => listing.isClaimed) //filter out the listings that are claimed
      });
    } catch (error) {
      console.error('Error fetching user listings:', error); //log the error
      setUserListings({ current: [], completed: [] }); //set the user listings to an empty array
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBuyingRequests = async () => {
    if (!user) return;
    try {
      // Fetch both received (as seller) and sent (as buyer) requests
      const [receivedRes, sentRes] = await Promise.all([
        fetch(`${API_BASE}/buying-requests/received`, { credentials: 'include' }),
        fetch(`${API_BASE}/buying-requests/sent`, { credentials: 'include' })
      ]);

      let allRequests = [];
      
      if (receivedRes.ok) {
        const receivedData = await receivedRes.json();
        // Include *all* requests (pending + completed) so we can show recent completions too
        allRequests.push(...receivedData.map(req => ({ ...req, userRole: 'seller' })));
      }
      
      if (sentRes.ok) {
        const sentData = await sentRes.json();
        allRequests.push(...sentData.map(req => ({ ...req, userRole: 'buyer' })));
      }

      // Sort by most recent first
      allRequests.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      
      console.log('🛒 All buying requests:', allRequests);
      setBuyingRequests(allRequests);
    } catch (err) {
      console.error('Fetch buying requests error:', err);
    }
  };

  const fetchSavedListings = async () => {
    if (!user) return;
    try {
      const res = await fetch(`${API_BASE}/listings/saved/me`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSavedListings(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Fetch saved listings error:', err);
    }
  };

  // Set of saved listing ids for quick lookup when rendering cards
  const savedIds = new Set(savedListings.map(l => l._id));

  // Toggle a listing's saved state (optimistic) and persist to the backend.
  const handleToggleSave = async (listing, shouldSave) => {
    // Optimistic local update
    setSavedListings(prev =>
      shouldSave
        ? (prev.some(l => l._id === listing._id) ? prev : [listing, ...prev])
        : prev.filter(l => l._id !== listing._id)
    );
    try {
      const res = await fetch(`${API_BASE}/listings/${listing._id}/save`, {
        method: shouldSave ? 'POST' : 'DELETE',
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Request failed');
      toast.success(shouldSave ? 'Saved to your dashboard' : 'Removed from saved');
    } catch (err) {
      console.error('Toggle save error:', err);
      toast.error('Could not update saved listings.');
      // Roll back on failure
      setSavedListings(prev =>
        shouldSave
          ? prev.filter(l => l._id !== listing._id)
          : (prev.some(l => l._id === listing._id) ? prev : [listing, ...prev])
      );
    }
  };

  const sortByLatest = (arr) => { //sortByLatest is the function to sort the listings by latest
    return [...arr].sort((a, b) => { //sort the listings by latest
      const dateA = new Date(a.updatedAt || a.createdAt); //get the date of the listing
      const dateB = new Date(b.updatedAt || b.createdAt); //get the date of the listing
      return dateB - dateA; // Newest first
    });
  };

  const filterListings = () => {
    if (selectedCategory === 'user-dashboard') {
      // Show all user's listings (both active and completed) sorted by latest activity
      const combined = [...userListings.current, ...userListings.completed];
      setFilteredListings(sortByLatest(combined));
    } else if (searchQuery.trim()) {
      // Global search: backend-filtered results, independent of selected category
      setFilteredListings(sortByLatest(searchResults || []));
    } else if (selectedCategory === 'all') {
      // Show all active marketplace listings sorted by latest activity
      setFilteredListings(sortByLatest(allListings));
    } else {
      // Show marketplace listings by category sorted by latest activity
      const filtered = allListings.filter(listing => listing.category === selectedCategory);
      setFilteredListings(sortByLatest(filtered));
    }
  };

  // Admin-only: fetch holds if owner email
  useEffect(() => {
    if (!user || (user.email || '').toLowerCase() !== 'hchhabra@umass.edu') return;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/holds`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setHolds(data || []);
        }
      } catch (e) {
        console.error('Failed to load holds', e);
      }
    })();
  }, [user]);

  const handleDelete = (listingId) => {
    setConfirmModal({
      open: true,
      title: 'Delete listing?',
      message: 'This listing will be removed permanently. If it has a pending transaction, cancel that first — the buyer will be notified.',
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: () => executeDelete(listingId),
    });
  };

  const executeDelete = async (listingId) => {
    try {
      const response = await fetch(`${API_BASE}/listings/${listingId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        const data = await response.json().catch(()=>({}));
        throw new Error(data.message || 'Failed to delete listing'); //if the response is not ok, throw an error
      }
      
      // Update both user listings and all listings
      setUserListings(prev => ({
        ...prev,
        current: prev.current.filter(listing => listing._id !== listingId) //filter out the listing that is being deleted
      }));
      setAllListings(prev => prev.filter(listing => listing._id !== listingId)); //filter out the listing that is being deleted
    } catch (error) {
      console.error('Error deleting listing:', error); //log the error
      toast.error('Error deleting listing: ' + error.message);
    }
  };

  const handleEdit = (listing) => { //handleEdit is the function to edit the listing
    setEditingListing(listing); //set the editing listing to the state
    setShowListingForm(true); //set the show listing form to true
  };

  const handleListingSubmit = async (listingData) => { //handleListingSubmit is the function to submit the listing
    console.log('🚀 Dashboard: Handling listing submission'); //log the submission
    console.log('📝 Editing existing listing:', !!editingListing); //log the editing listing
    console.log('📋 Listing data received from form:', { //log the listing data
      title: listingData.title,
      images: listingData.images?.length || 0, //log the images
      video: listingData.video ? listingData.video.type : 'none', //log the video
      thumbnailImage: !!listingData.thumbnailImage //log the thumbnail image
    });

    try {
      const url = editingListing 
        ? `${API_BASE}/listings/${editingListing._id}` //if the listing is being edited, use the id of the listing
        : `${API_BASE}/listings`; //if the listing is not being edited, use the url of the listing
      
      // Clean the data before sending to server
      const cleanedData = { ...listingData };
      
      // Remove video field if it's null to avoid validation errors
      if (!cleanedData.video) {
        delete cleanedData.video;
        console.log('🧹 Removed null video field from submission');
      }
      
      // Remove any other null/undefined fields that might cause issues
      Object.keys(cleanedData).forEach(key => {
        if (cleanedData[key] === null || cleanedData[key] === undefined) {
          delete cleanedData[key];
          console.log(`🧹 Removed null ${key} field from submission`);
        }
      });

      console.log('🌐 Sending request to:', url);
      console.log('📤 Request method:', editingListing ? 'PUT' : 'POST');
      console.log('📦 Cleaned request body (preview):', {
        title: cleanedData.title,
        description: cleanedData.description?.substring(0, 100) + '...',
        images: cleanedData.images?.map(img => ({ 
          url: img.url?.substring(0, 50) + '...', 
          publicId: img.publicId 
        })),
        video: cleanedData.video ? cleanedData.video.type : 'REMOVED',
        thumbnailImage: cleanedData.thumbnailImage?.substring(0, 50) + '...'
      });

      const response = await fetch(url, {
        method: editingListing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(cleanedData)
      });

      console.log('Server response status:', response.status);
      console.log('Server response ok:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.log('❌ Server error response:', errorText);
        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }
      
      const savedListing = await response.json();
      console.log('✅ Listing saved successfully:', {
        id: savedListing._id,
        title: savedListing.title,
        hasImages: !!savedListing.images?.length,
        hasVideo: !!savedListing.video
      });

      const addOrUpdate = (arr) => {
        const idx = arr.findIndex(l => l._id === savedListing._id);
        if (idx === -1) {
          return [savedListing, ...arr];
        }
        const newArr = [...arr];
        newArr[idx] = savedListing;
        return newArr;
      };

      // Update user listings (no duplicates)
      setUserListings(prev => ({
        ...prev,
        current: addOrUpdate(prev.current)
      }));

      // Update all listings (no duplicates)
      setAllListings(prev => addOrUpdate(prev));

      console.log('UI state updated successfully');
      console.log('Dashboard: Closing form modal');
      setShowListingForm(false);
      setEditingListing(null);
    } catch (error) {
      console.error('💥 Error saving listing:', error);
      console.error('💥 Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      toast.error('Error saving listing: ' + error.message);
    }
  };

  const handleLogout = async () => {     //handleLogout is the function that is called when the logout button is clicked
    try {
      await fetch(`${AUTH_BASE}/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      
      setIsAuthenticated(false); //set the isAuthenticated to false
      setUser(null); //set the user to null
      navigate('/login'); //navigate to the login page
    } catch (error) {
      console.error('Error logging out:', error); //log the error
      toast.error('Error logging out. Please try again.');
    }
  };

  const handleProfileUpdate = (updatedProfile) => {     //handleProfileUpdate is the function that is called when the profile is updated
    console.log('Dashboard received profile update:', updatedProfile);
    // Update the user state with the new profile information
    setUser(prevUser => ({
      ...prevUser,
      role: updatedProfile.role, //set the role to the new role
      campus: updatedProfile.campus, //set the campus to the new campus
      bio: updatedProfile.bio //set the bio to the new bio
    }));
  };

  const handleCategoryClick = (categoryId) => {     //handleCategoryClick is the function that is called when the category is clicked
    setSelectedCategory(categoryId); //set the selected category to the new category
    setSidebarOpen(false); // Close sidebar on mobile after selection
  };

  // Real-time event handlers
  const handleListingCreated = (newListing) => {     //handleListingCreated is the function that is called when a new listing is created
    console.log('🔥 Real-time: New listing created:', newListing.title); //log the new listing

    const exists = (arr, id) => arr.some(l => l._id === id); //exists is the function that is called when a new listing is created

    // Add to allListings if not already present
    setAllListings(prev => exists(prev, newListing._id) ? prev : [newListing, ...prev]); //set the all listings to the new listing

    // If the new listing belongs to the current user, ensure it's present in userListings.current without duplication
    const newOwnerId = typeof newListing.ownerId === 'object' ? newListing.ownerId._id : newListing.ownerId; //newOwnerId is the id of the new listing
    if (newOwnerId === user._id) { //if the new listing belongs to the current user
      setUserListings(prev => {
        if (exists(prev.current, newListing._id)) return prev; //if the new listing is already in the current listings, return the current listings
        return { ...prev, current: [newListing, ...prev.current] }; //set the user listings to the new listing
      });
    }
  };

  const handleListingUpdated = (updatedListing) => {     //handleListingUpdated is the function that is called when a listing is updated
    console.log('🔥 Real-time: Listing updated:', updatedListing.title);
    setAllListings(prev => 
      prev.map(listing => 
        listing._id === updatedListing._id ? updatedListing : listing
      )
    );
    setUserListings(prev => ({   //set the user listings to the new listing
      current: prev.current.map(listing =>
        listing._id === updatedListing._id ? updatedListing : listing
      ),
      completed: prev.completed.map(listing =>
        listing._id === updatedListing._id ? updatedListing : listing
      )
    }));
    // Keep the saved section to still-available items only
    setSavedListings(prev => {
      const stillAvailable = updatedListing.status === 'available' && !updatedListing.pendingBuyingRequest && !updatedListing.isClaimed;
      if (stillAvailable) {
        return prev.map(l => l._id === updatedListing._id ? updatedListing : l);
      }
      return prev.filter(l => l._id !== updatedListing._id);
    });
  };

  const handleListingDeleted = (deletedData) => {     //handleListingDeleted is the function that is called when a listing is deleted
    console.log(' Real-time: Listing deleted:', deletedData.title); //log the deleted listing
    setAllListings(prev => prev.filter(listing => listing._id !== deletedData._id)); //set the all listings to the new listing
    setSavedListings(prev => prev.filter(listing => listing._id !== deletedData._id));
    setUserListings(prev => ({
      current: prev.current.filter(listing => listing._id !== deletedData._id),
      completed: prev.completed.filter(listing => listing._id !== deletedData._id)
    }));
  };

  const handleListingClaimed = (claimedListing) => {
    console.log(' Real-time: Listing claimed:', claimedListing.title);
    
    // Remove from allListings since claimed items aren't shown in marketplace
    setAllListings(prev => prev.filter(listing => listing._id !== claimedListing._id));
    // Drop from saved — a claimed item is no longer available
    setSavedListings(prev => prev.filter(listing => listing._id !== claimedListing._id));
    
    // Move from current to completed in userListings if it's the user's listing
    setUserListings(prev => {
      const isUserListing = prev.current.some(listing => listing._id === claimedListing._id);
      if (isUserListing) {
        return {
          current: prev.current.filter(listing => listing._id !== claimedListing._id),
          completed: [...prev.completed, claimedListing]
        };
      }
      return prev;
    });
  };

  // Set up socket connection when user is loaded
  useEffect(() => {
    if (user) {
      // Connect to socket
      socketService.connect(user._id);
      
      // Add real-time event listeners
      socketService.addEventListener('listing_created', handleListingCreated);
      socketService.addEventListener('listing_updated', handleListingUpdated);
      socketService.addEventListener('listing_deleted', handleListingDeleted);
      socketService.addEventListener('listing_claimed', handleListingClaimed);
      socketService.addEventListener('purchase_paid', handlePurchasePaid);
      socketService.addEventListener('sale_paid', handleSalePaid);
      socketService.addEventListener('purchase_captured', handlePurchaseCaptured);
      socketService.addEventListener('sale_captured', handleSaleCaptured);
      socketService.addEventListener('transaction_completed', handleTransactionCompleted);
      socketService.addEventListener('buying_request_approved', handleBuyingRequestApproved);
      socketService.addEventListener('buying_request_declined', handleBuyingRequestDeclined);
      socketService.addEventListener('buying_request_received', handleBuyingRequestReceived);
      
      console.log('🚀 Socket connected and listeners added for user:', user.displayName);
    }

    // Cleanup function
    return () => {
      if (user) {
        socketService.removeEventListener('listing_created', handleListingCreated);
        socketService.removeEventListener('listing_updated', handleListingUpdated);
        socketService.removeEventListener('listing_deleted', handleListingDeleted);
        socketService.removeEventListener('listing_claimed', handleListingClaimed);
        socketService.removeEventListener('purchase_paid', handlePurchasePaid);
        socketService.removeEventListener('sale_paid', handleSalePaid);
        socketService.removeEventListener('purchase_captured', handlePurchaseCaptured);
        socketService.removeEventListener('sale_captured', handleSaleCaptured);
        socketService.removeEventListener('transaction_completed', handleTransactionCompleted);
        socketService.removeEventListener('buying_request_approved', handleBuyingRequestApproved);
        socketService.removeEventListener('buying_request_declined', handleBuyingRequestDeclined);
        socketService.removeEventListener('buying_request_received', handleBuyingRequestReceived);
      }
    };
  }, [user]); // Depend on user to set up socket when user loads

  // Cleanup socket on component unmount
  useEffect(() => {
    return () => {
      socketService.disconnect();
    };
  }, []);

  const openChat = async (listing) => {
    try {
      const ownerId = typeof listing.ownerId === 'object' ? listing.ownerId._id : listing.ownerId; //This is the id of the user who owns the listing
      const room = await chatService.ensureRoom(ownerId); //This is the room that is being created
      setChatRoom(room); //set the chat room to the state
      setChatModalOpen(true); //set the chat modal to true
      setDrawerOpen(true);
      openRoom(room);
    } catch (err) {
      console.error('Error opening chat:', err);
    }
  };

  const openSellerProfile = (listing) => {
    let sellerId;
    console.log('[openSellerProfile] Listing:', listing);
    if (typeof listing.ownerId === 'object' && listing.ownerId !== null) {
      // Owner is populated - use the _id field instead of uid
      sellerId = listing.ownerId._id;
      /**
       * When you tried to view a seller's profile, the code was looking for 
       * listing.ownerId.uid, but your listing data structure doesn't have a uid 
       * property in the ownerId object. Instead, it has _id.
       * "ownerId": {
    "_id": "6839f8fbee4387ce593a2c7e",
    "email": "hchhabra@umass.edu",
    "displayName": "Himnish Chhabra",
    "profilePicture": "https://lh3.googleusercontent.com/..."
}
       */
      console.log('[openSellerProfile] Populated ownerId, using _id:', sellerId);
    } else {
      // Owner is not populated - ownerId is just an ObjectId string
      // We'll need to use this ObjectId to fetch the user's uid
      console.warn('[openSellerProfile] Owner not populated for listing:', listing.title);
      sellerId = listing.ownerId; // This will be an ObjectId, but we'll handle it in the backend
    }
    if (!sellerId) {
      console.error('[openSellerProfile] Could not determine seller ID for listing:', listing);
      return;
    }
    setSelectedSellerId(sellerId);
    setSellerProfileModalOpen(true);
    console.log('[openSellerProfile] Set selectedSellerId:', sellerId, 'Modal open:', true);
  };

  const unreadTotal = rooms.reduce((sum, r) => sum + (r.unread||0),0);

  // Requests that need the user's attention — drives the sidebar + burger badges.
  const dashboardAttentionCount = buyingRequests.filter(req =>
    (req.status === 'pending' && req.userRole === 'seller') ||  // incoming request to approve/decline
    (req.status === 'approved') ||                              // reserved: meet up / enter PIN
    (req.status === 'declined' && req.userRole === 'buyer') ||  // unseen "not accepted" notice
    (req.status === 'expired')                                  // unseen "expired" notice
  ).length;
  const totalNotifications = dashboardAttentionCount + unreadTotal;

  // Deep-link from emails: /dashboard?view=requests opens Your Dashboard; ?view=messages opens chat.
  // Shared links: /dashboard?listing=<id> opens that listing in a focused modal.
  // Params may arrive in the URL directly, or via a stash App.js saved before the login/redirect
  // round-trip stripped the query. Merge both, preferring whatever is already in the URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    try {
      const raw = localStorage.getItem('utrade_pendingDeepLink');
      if (raw) {
        localStorage.removeItem('utrade_pendingDeepLink'); // consume once
        const { q, t } = JSON.parse(raw);
        // Ignore anything older than 10 minutes so a stale stash can't hijack a later login.
        if (q && t && Date.now() - t < 10 * 60 * 1000) {
          const stored = new URLSearchParams(q);
          if (!params.get('view') && stored.get('view')) params.set('view', stored.get('view'));
          if (!params.get('listing') && stored.get('listing')) params.set('listing', stored.get('listing'));
        }
      }
    } catch (e) { /* ignore storage/parse errors */ }

    const view = params.get('view');
    if (view === 'requests' || view === 'dashboard') setSelectedCategory('user-dashboard');
    else if (view === 'messages') setDrawerOpen(true);

    const listingId = params.get('listing');
    if (listingId) {
      (async () => {
        try {
          const res = await fetch(`${API_BASE}/listings/${listingId}`, { credentials: 'include' });
          if (res.ok) setDeepLinkedListing(await res.json());
        } catch (err) {
          console.error('Deep-link listing fetch error:', err);
        }
      })();
    }
  }, []);

  // -----------------------------
  // Derived data & helper methods
  // -----------------------------
  // Direct transactions (non-Stripe)
  const activeDirectRequests = buyingRequests.filter(req => req.status !== 'completed');
  const completedDirectRequests = buyingRequests.filter(
    req => req.status === 'completed' && !dismissedDirectCompletedIds.includes(req._id)
  );

  // Stripe: recently-completed purchases/sales minus ones the user has dismissed
  const visibleCompletedStripeTransactions = completedTransactions
    .filter(tx => tx.method==='stripe')
    .filter(tx => !dismissedStripeCompletedIds.includes(tx.listing?._id))
    .filter((tx, idx, arr) => {
      const id = tx.listing?._id;
      return id ? arr.findIndex(t => t.listing && t.listing._id === id) === idx : true;
    });

  const visibleCompletedDirectTransactions = completedTransactions
    .filter(tx => tx.method==='direct')
    .filter(tx => !dismissedDirectCompletedIds.includes(tx.listing?._id))
    .filter((tx, idx, arr) => {
      const id = tx.listing?._id;
      return id ? arr.findIndex(t => t.listing && t.listing._id === id) === idx : true;
    });

  // Dismiss helpers – keep state local so history remains intact
  const dismissCompletedDirect = (requestId) =>
    setDismissedDirectCompletedIds(prev => [...prev, requestId]);

  const dismissCompletedStripe = (listingId) =>
    setDismissedStripeCompletedIds(prev => [...prev, listingId]);

  // Stripe onboarding removed in Phase 1 (ENABLE_STRIPE=false)
  const handleCreateListingClick = () => {
    setShowListingForm(true);
  };

  const handlePurchasePaid = (payload) => {
    console.log('🔔 Socket: purchase_paid received', payload);
    if (payload && payload.purchaseId) {
      setActivePurchases(prev => {
        const exists = prev.some(p => p._id === payload.purchaseId);
        if (exists) return prev;
        if(!payload.listing) return prev;
        return [{ _id: payload.purchaseId, pin: payload.pin, listing: payload.listing, sellerName: payload.sellerName }, ...prev];
      });
    }

    // Notify buyer of payment success
    pushNotification({
      id: Date.now(),
      listingId: payload.listing?._id || 'stripe',
      message: 'Payment successful! PIN has been generated.',
      type: 'success',
      timestamp: new Date()
    });
  };

  const handleSalePaid = (payload) => {
    console.log('🔔 Socket: sale_paid received', payload);
    if (payload && payload.purchaseId) {
      setActiveSales(prev => {
        const exists = prev.some(p => p._id === payload.purchaseId);
        if (exists) return prev;
        if(!payload.listing) return prev;
        return [{ _id: payload.purchaseId, listing: payload.listing, buyerName: payload.buyerName }, ...prev];
      });
    }
  };

  const handleSaleCaptured = (payload) => {
    console.log('✅ sale_captured', payload);
    if (!payload?.purchaseId) return;
    setActiveSales(prev => prev.filter(p => p._id !== payload.purchaseId));
    if(payload.listing){
      setCompletedTransactions(prev=>[{method:'stripe', role:'seller', listing: payload.listing}, ...prev]);
      pushRecentCompletion('stripe', { id: payload.purchaseId || Date.now(), role:'seller', title: payload.listing.title });
      // Refresh transaction history
      fetchTransactionHistory();
    }
  };

  const handlePurchaseCaptured = (payload) => {
    console.log('✅ purchase_captured', payload);
    if (!payload?.purchaseId) return;
    setActivePurchases(prev => prev.filter(p => p._id !== payload.purchaseId));
    if(payload.listing){
      setCompletedTransactions(prev=>[{method:'stripe', role:'buyer', listing: payload.listing}, ...prev]);
      pushRecentCompletion('stripe', { id: payload.purchaseId || Date.now(), role:'buyer', title: payload.listing.title });
      // Refresh transaction history
      fetchTransactionHistory();
    }
  };

  const handleTransactionCompleted = (payload) => {
    console.log('✅ transaction_completed', payload);
    if (!payload?.requestId) return;
    
    // Add success notification
    const notification = {
      id: Date.now(),
      listingId: payload.listing?._id || 'direct',
      message: payload.message || 'Transaction completed successfully!',
      type: 'success',
      timestamp: new Date()
    };
    pushNotification(notification);
    
    // Inline banner
    if (payload.listing) {
      const role = payload.listing.ownerId === user._id ? 'seller' : 'buyer';
      pushRecentCompletion('direct', { id: payload.requestId, role, title: payload.listing.title });
      setCompletedTransactions(prev=>[{method:'direct', role, listing: payload.listing}, ...prev]);
      setTxModal({open:true,message:'Transaction completed successfully!',type:'success'});
    }
    
    // Refresh buying requests and transaction history
    fetchBuyingRequests();
    fetchTransactionHistory();
  };

  const pushNotification = (notif) => {
    const type = ['success','error','warning'].includes(notif.type) ? notif.type : 'info';
    toast[type](notif.message);
  };

  // legacy compat — Snackbar replaced by ToastContext
  const handleSnackbarClose = () => {};

  const handleBuyingRequestApproved = (payload) => {
    console.log('✅ buying_request_approved', payload);
    // Refresh buying requests to show updated status
    fetchBuyingRequests();
    pushNotification({
      id: Date.now(),
      listingId: payload.listing?._id || 'approved',
      message: `Your item request for "${payload.listing?.title}" was approved!`,
      type: 'success',
      timestamp: new Date()
    });
  };

  const handleBuyingRequestDeclined = (payload) => {
    console.log('❌ buying_request_declined', payload);
    // Refresh buying requests to show updated status
    fetchBuyingRequests();
    pushNotification({
      id: Date.now(),
      listingId: payload.listing?._id || 'declined',
      message: `Your item request for "${payload.listing?.title}" wasn’t accepted.`,
      type: 'warning',
      timestamp: new Date()
    });
  };

  const handleBuyingRequestReceived = (payload) => {
    console.log('📩 buying_request_received', payload);
    // Refresh received requests list (for sellers)
    fetchBuyingRequests();
    pushNotification({
      id: Date.now(),
      listingId: payload.listing?._id || 'received',
      message: `New item request received for "${payload.listing?.title}"`,
      type: 'warning',
      timestamp: new Date()
    });
  };

  const fetchTransactionHistory = async () => {
    if (!user) return;
    try {
      const res = await fetch(`${API_BASE}/purchases/history`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        console.log('📜 Transaction history refreshed:', data);
        setTransactionHistory(data);

        // Ensure recently completed Stripe transactions are visible after refresh
        const stripeCompletions = data
          .filter(t => t.paymentMethod === 'stripe')
          .map(t => ({
            role: t.role,
            listing: {
              _id: t.id, // fallback key
              title: t.listingTitle,
              itemId: t.itemId
            }
          }));
        setCompletedTransactions(prev => {
          const existingIds = new Set(prev.map(c => c.listing._id));
          const merged = [...prev];
          stripeCompletions.forEach(c => {
            if (!existingIds.has(c.listing._id)) merged.push(c);
          });
          return merged;
        });
      }
    } catch (err) {
      console.error('Fetch transaction history error:', err);
    }
  };

  const confirmPin = async (purchaseId) => {
    const pin = pinInputs[purchaseId];
    if (!pin || pin.length !== 6) {
      setTxModal({open:true,message:'Please enter a 6-digit PIN',type:'error'});
      return;
    }

    try {
      console.log('Confirming PIN for purchase:', purchaseId, 'PIN:', pin);
      const res = await fetch(`${API_BASE}/purchases/${purchaseId}/confirm-pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ pin })
      });
      const data = await res.json();
      if (!res.ok) {
        pushNotification({ id: Date.now(), message: data.message || 'Invalid PIN', type: 'error', timestamp: new Date() });
        return;
      }
      setTxModal({open:true,message:'Transaction completed successfully!',type:'success'});
      // Clear the PIN input for this purchase
      setPinInputs(prev => {
        const newInputs = { ...prev };
        delete newInputs[purchaseId];
        return newInputs;
      });
      // Will be removed by socket handler; but fallback update:
      setActiveSales(prev => prev.filter(p => p._id !== purchaseId));
      if (data && data.listing) {
        // Immediately reflect in recently completed UI in case socket event is delayed
        setCompletedTransactions(prev => [{ role: 'seller', listing: data.listing }, ...prev]);
        pushRecentCompletion('stripe', { id: purchaseId, role: 'seller', title: data.listing.title });
      }
    } catch (err) {
      console.error('Confirm PIN error', err);
      pushNotification({ id: Date.now(), message: 'Server error confirming PIN', type: 'error', timestamp: new Date() });
    }
  };

  // Buying Request Functions
  const handleApproveBuyingRequest = async (requestId) => {
    try {
      const res = await fetch(`${API_BASE}/buying-requests/${requestId}/approve`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.message || 'Failed to approve request');
        return;
      }
      pushNotification({
        id: Date.now(),
        listingId: requestId,
        message: 'Buying request approved! The buyer has been notified and given a PIN.',
        type: 'success',
        timestamp: new Date()
      });
      
      // Refresh buying requests
      await fetchBuyingRequests();
    } catch (err) {
      console.error('Approve request error:', err);
      toast.error('Server error approving request');
    }
  };

  const handleDeclineBuyingRequest = async (requestId) => {
    try {
      const res = await fetch(`${API_BASE}/buying-requests/${requestId}/decline`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.message || 'Failed to decline request');
        return;
      }
      pushNotification({
        id: Date.now(),
        listingId: requestId,
        message: 'Request declined. The buyer has been notified.',
        type: 'info',
        timestamp: new Date()
      });
      
      // Refresh buying requests
      await fetchBuyingRequests();
    } catch (err) {
      console.error('Decline request error:', err);
      toast.error('Server error declining request');
    }
  };

  const confirmBuyingRequestPin = async (requestId, pin) => {
    if (!pin || pin.length !== 6) {
      toast.warning('Please enter a 6-digit PIN');
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/buying-requests/${requestId}/confirm-pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ pin })
      });
      const data = await res.json();
      if (!res.ok) {
        pushNotification({ id: Date.now(), message: data.message || 'Invalid PIN', type: 'error', timestamp: new Date() });
        return;
      }
      
      // Transaction completion handled by socket event - no duplicate notification needed
      
      // Refresh buying requests and transaction history
      await fetchBuyingRequests();
      fetchTransactionHistory();
      // Fallback: ensure recently completed shows up instantly for seller side
      if (data && data.listing) {
        pushRecentCompletion('direct', { id: requestId, role: 'seller', title: data.listing.title });
        // Update buyingRequests state so UI moves item immediately into completed subsection
        setBuyingRequests(prev => prev.map(r => r._id === requestId ? { ...r, status: 'completed' } : r));
      }
    } catch (err) {
      console.error('Confirm buying request PIN error:', err);
      pushNotification({ id: Date.now(), message: 'Server error confirming PIN', type: 'error', timestamp: new Date() });
    }
  };

  const dismissTransactionNotification = (notificationId) => {
    // No need to update transactionNotifications as it's now handled by snackbar
  };

  const dismissBuyingRequest = async (requestId) => {
    try {
      const res = await fetch(`${API_BASE}/buying-requests/${requestId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (!res.ok) {
        const data = await res.json();
        toast.error(data.message || 'Failed to dismiss request');
        return;
      }
      
      // Remove from local state immediately
      setBuyingRequests(prev => prev.filter(req => req._id !== requestId));
    } catch (err) {
      console.error('Dismiss request error:', err);
      toast.error('Server error dismissing request');
    }
  };

  // ----------------------------
  // Persist dismissed IDs locally
  // ----------------------------
  useEffect(() => {
    try {
      const storedDirect = JSON.parse(localStorage.getItem('utrade_dismissedDirect')) || [];
      const storedStripe = JSON.parse(localStorage.getItem('utrade_dismissedStripe')) || [];
      if (storedDirect.length) setDismissedDirectCompletedIds(storedDirect);
      if (storedStripe.length) setDismissedStripeCompletedIds(storedStripe);
    } catch(e) { console.error('Failed to load dismissed IDs', e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    localStorage.setItem('utrade_dismissedDirect', JSON.stringify(dismissedDirectCompletedIds));
  }, [dismissedDirectCompletedIds]);

  useEffect(() => {
    localStorage.setItem('utrade_dismissedStripe', JSON.stringify(dismissedStripeCompletedIds));
  }, [dismissedStripeCompletedIds]);

  // Helper to show temporary completion banners (15s default)
  const pushRecentCompletion = (type, { id, role, title }, ttl = 15000) => {
    const setter = type === 'stripe' ? setRecentStripeCompletions : setRecentDirectCompletions;
    setter(prev => [...prev, { id, role, title }]);
    setTimeout(() => setter(prev => prev.filter(c => c.id !== id)), ttl);
  };

  const TxResultModal = () => {
    if(!txModal.open) return null;
    const isSuccess = txModal.type==='success';
    const bgColor = isSuccess ? '#d4edda' : '#f8d7da';
    const iconColor = isSuccess ? '#2e7d32' : '#c53030';
    return ReactDOM.createPortal(
      <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',justifyContent:'center',alignItems:'center',zIndex:4000}} onClick={()=>setTxModal(prev=>({...prev,open:false}))}>
        <div style={{background:'#fff',borderRadius:'12px',padding:'2rem',maxWidth:'420px',width:'90%',boxShadow:'0 10px 30px rgba(0,0,0,0.25)'}} onClick={e=>e.stopPropagation()}>
          <div style={{display:'flex',justifyContent:'center',alignItems:'center',width:'64px',height:'64px',borderRadius:'50%',background:bgColor,margin:'0 auto 1rem'}}>
            <span style={{fontSize:'2rem',color:iconColor}}>{isSuccess?'✓':'✕'}</span>
          </div>
          <h3 style={{textAlign:'center',margin:'0 0 1rem',color:iconColor}}>{txModal.message}</h3>
          <div style={{display:'flex',justifyContent:'center'}}>
            <button onClick={()=>setTxModal(prev=>({...prev,open:false}))} style={{padding:'0.6rem 1.25rem',background:'var(--maroon)',color:'#fff',border:'none',borderRadius:'8px',cursor:'pointer'}}>Close</button>
          </div>
        </div>
      </div>, document.body);
  };

  if (!user || isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: 'var(--surface-bg)',
        fontFamily: 'var(--font)'
      }}>
        <div style={{ textAlign:'center' }}>
          <LoadingSpinner size={60}/>
          <div style={{ marginTop:'1rem', color:'var(--maroon)', fontWeight:600 }}>{!user ? 'Initializing...' : 'Loading marketplace...'}</div>
        </div>
      </div>
    );
  }

  const navItemStyle = (active) => ({
    width: '100%',
    padding: '0.7rem 0.9rem',
    backgroundColor: active ? 'var(--maroon-light)' : 'transparent',
    color: active ? 'var(--maroon)' : 'var(--text-strong)',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    marginBottom: '0.35rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    fontSize: '0.95rem',
    textAlign: 'left',
    fontWeight: active ? 600 : 500,
    fontFamily: 'inherit',
    transition: 'background-color 0.2s ease, color 0.2s ease'
  });
  const navHoverOn = (e, active) => { if (!active) e.currentTarget.style.backgroundColor = 'var(--surface-panel)'; };
  const navHoverOff = (e, active) => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; };
  const navSectionLabel = {
    margin: '0 0 0.8rem 0',
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    color: 'var(--text-soft)',
    letterSpacing: '1px',
    fontWeight: 600
  };

  return (
    <>
    <TxResultModal />
    {/* Deep-linked listing from a shared ?listing=<id> URL — opens in focused view */}
    {deepLinkedListing && (
      <ListingCard
        listing={deepLinkedListing}
        currentUser={user}
        initialFocused
        showActions={false}
        onChat={openChat}
        onViewProfile={openSellerProfile}
        isSaved={savedIds.has(deepLinkedListing._id)}
        onToggleSave={handleToggleSave}
        onDelete={() => {}}
        onEdit={null}
        onCloseFocus={() => {
          setDeepLinkedListing(null);
          // Drop the ?listing param so refresh/back doesn't reopen it
          const url = new URL(window.location.href);
          url.searchParams.delete('listing');
          window.history.replaceState({}, '', url.pathname + url.search);
        }}
      />
    )}
    <div style={{
      display: 'flex',
      minHeight: '100vh',
      backgroundColor: 'var(--surface-bg)',
      fontFamily: 'var(--font)'
    }}>
      {/* Sidebar */}
      <div style={{
        width: sidebarOpen ? (isMobile ? 'min(82vw, 320px)' : '280px') : '0',
        backgroundColor: 'var(--surface-card)',
        color: 'var(--text-strong)',
        borderRight: sidebarOpen ? '1px solid var(--border-subtle)' : 'none',
        transition: 'width 0.3s ease',
        overflowX: 'hidden',
        overflowY: sidebarOpen ? 'auto' : 'hidden',
        position: 'fixed',
        height: '100vh',
        zIndex: 1000,
        boxShadow: sidebarOpen ? '4px 0 24px rgba(32,32,32,0.06)' : 'none'
      }}>
        <div style={{ padding: '1.5rem' }}>
          {/* Sidebar Header */}
          <div style={{
            borderBottom: '1px solid var(--border-subtle)',
            paddingBottom: '1rem',
            marginBottom: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <ProfilePicture 
              src={user.profilePicture}
              alt={user.displayName || 'Student'}
              size="small"
              fallbackColor="var(--maroon)"
              style={{ flexShrink: 0 }}
            />
            <div>
              <h2 style={{ margin: '0 0 0.25rem 0', fontSize: '1.5rem', color: 'var(--maroon)', letterSpacing: '-0.5px' }}>UTrade</h2>
              <p style={{ margin: '0', color: 'var(--text-soft)', fontSize: '0.85rem' }}>
                {user.displayName || 'Student'}
              </p>
            </div>
          </div>

          {/* Navigation Menu */}
          <nav>
            {/* Your Dashboard Section */}
            <div style={{ marginBottom: '1.75rem' }}>
              <h3 style={navSectionLabel}>Your Account</h3>
              <button
                onClick={() => {
                  setSelectedCategory('user-dashboard');
                  setSidebarOpen(false);
                }}
                style={navItemStyle(selectedCategory === 'user-dashboard')}
                onMouseOver={(e) => navHoverOn(e, selectedCategory === 'user-dashboard')}
                onMouseOut={(e) => navHoverOff(e, selectedCategory === 'user-dashboard')}
              >
                Your Dashboard{dashboardAttentionCount > 0 ? ` (${dashboardAttentionCount})` : ''}
              </button>

              {/* Mobile-only: actions that live in the header on desktop */}
              {isMobile && (
                <>
                  <button
                    onClick={() => { setSidebarOpen(false); setProfileModalOpen(true); }}
                    style={navItemStyle(false)}
                    onMouseOver={(e) => navHoverOn(e, false)}
                    onMouseOut={(e) => navHoverOff(e, false)}
                  >
                    Profile
                  </button>
                  <button
                    onClick={() => { setSidebarOpen(false); setDrawerOpen(true); }}
                    style={navItemStyle(false)}
                    onMouseOver={(e) => navHoverOn(e, false)}
                    onMouseOut={(e) => navHoverOff(e, false)}
                  >
                    Messages{unreadTotal > 0 ? ` (${unreadTotal})` : ''}
                  </button>
                </>
              )}
            </div>

            {/* Browse Marketplace Section */}
            <div style={{ marginBottom: '1.75rem' }}>
              <h3 style={navSectionLabel}>Browse Marketplace</h3>
              <button
                onClick={() => handleCategoryClick('all')}
                style={navItemStyle(selectedCategory === 'all')}
                onMouseOver={(e) => navHoverOn(e, selectedCategory === 'all')}
                onMouseOut={(e) => navHoverOff(e, selectedCategory === 'all')}
              >
                <span style={{ fontSize: '1.1rem' }}>🛍️</span>
                All Listings
              </button>

              {paidCategories.map(category => (
                <button
                  key={category.id}
                  onClick={() => handleCategoryClick(category.id)}
                  style={navItemStyle(selectedCategory === category.id)}
                  onMouseOver={(e) => navHoverOn(e, selectedCategory === category.id)}
                  onMouseOut={(e) => navHoverOff(e, selectedCategory === category.id)}
                >
                  {category.name}
                </button>
              ))}
            </div>

            {/* Free Items – its own subsection for visibility */}
            {freeCategory && (
              <div style={{ marginBottom: '1.75rem' }}>
                <h3 style={navSectionLabel}>Free Goods</h3>
                <button
                  onClick={() => handleCategoryClick(freeCategory.id)}
                  style={navItemStyle(selectedCategory === freeCategory.id)}
                  onMouseOver={(e) => navHoverOn(e, selectedCategory === freeCategory.id)}
                  onMouseOut={(e) => navHoverOff(e, selectedCategory === freeCategory.id)}
                >
                  {freeCategory.name}
                </button>
              </div>
            )}
            {/* Admin section — only visible to admin users */}
            {user && (user.isAdmin || (user.email || '').toLowerCase() === process.env.REACT_APP_ADMIN_EMAIL || ['hchhabra@umass.edu'].includes((user.email || '').toLowerCase())) && (
              <div style={{ marginTop: '1rem', borderTop: '1px solid var(--border-subtle)', paddingTop: '1rem', marginBottom: '1.75rem' }}>
                <h3 style={navSectionLabel}>Admin</h3>
                <button
                  onClick={() => { window.location.href = '/admin'; }}
                  style={navItemStyle(false)}
                  onMouseOver={(e) => navHoverOn(e, false)}
                  onMouseOut={(e) => navHoverOff(e, false)}
                >
                  Admin Dashboard
                </button>
              </div>
            )}

            {/* Help and feedback links */}
            <div style={{ marginTop: '0.25rem', borderTop: '1px solid var(--border-subtle)', paddingTop: '1rem' }}>
              <h3 style={navSectionLabel}>Support</h3>
              <button
                onClick={() => navigate('/help')}
                style={navItemStyle(false)}
                onMouseOver={(e) => navHoverOn(e, false)}
                onMouseOut={(e) => navHoverOff(e, false)}
              >
                Help
              </button>

              <button
                onClick={() => window.open(FEEDBACK_FORM_URL, '_blank', 'noopener,noreferrer')}
                style={navItemStyle(false)}
                onMouseOver={(e) => navHoverOn(e, false)}
                onMouseOut={(e) => navHoverOff(e, false)}
              >
                Give Feedback
              </button>

              {isMobile && (
                <button
                  onClick={() => { setSidebarOpen(false); handleLogout(); }}
                  style={navItemStyle(false)}
                  onMouseOver={(e) => navHoverOn(e, false)}
                  onMouseOut={(e) => navHoverOff(e, false)}
                >
                  Log out
                </button>
              )}
            </div>
          </nav>
        </div>
      </div>

      {/* Mobile sidebar backdrop — overlays content instead of pushing it */}
      {isMobile && sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(32,32,32,0.45)',
            zIndex: 999
          }}
        />
      )}

      {/* Main Content */}
      <div style={{
        flex: 1,
        marginLeft: (sidebarOpen && !isMobile) ? '280px' : '0',
        minWidth: 0,
        transition: 'margin-left 0.3s ease'
      }}>
        {/* Top Header */}
        <header style={{
          backgroundColor: 'var(--surface-card)',
          padding: isMobile ? '0.75rem 1rem' : '1rem 1.5rem',
          borderBottom: '1px solid var(--border-subtle)',
          display: 'flex',
          flexWrap: isMobile ? 'wrap' : 'nowrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: isMobile ? '0.5rem' : '0',
          position: 'sticky',
          top: 0,
          zIndex: 900
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {/* Admin: Holds button (desktop only — hidden on mobile to save header space) */}
            {!isMobile && user && (user.email || '').toLowerCase() === 'hchhabra@umass.edu' && (
              <button
                onClick={async ()=>{
                  try {
                    const res = await fetch(`${API_BASE}/holds`, { credentials: 'include' });
                    if (res.ok) setHolds(await res.json());
                  } catch (e) {}
                }}
                style={{
                  padding:'0.55rem 1rem', backgroundColor:'transparent', border:'1px solid var(--maroon-border)', borderRadius:'10px', cursor:'pointer', fontSize:'0.9rem', color:'var(--maroon)', fontWeight:'600', fontFamily:'var(--font-heading)'
                }}
              >Holds</button>
            )}
            {/* Burger Menu Button */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-label={totalNotifications > 0 ? `Menu — ${totalNotifications} notifications` : 'Menu'}
              style={{
                padding: '0.5rem',
                backgroundColor: 'transparent',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                gap: '3px',
                position: 'relative'
              }}
            >
              {totalNotifications > 0 && (
                <span style={{
                  position: 'absolute', top: '-5px', right: '-5px',
                  background: 'var(--maroon)', color: '#fff', borderRadius: '999px',
                  fontSize: '0.65rem', fontWeight: 700, minWidth: '16px', height: '16px',
                  lineHeight: '16px', textAlign: 'center', padding: '0 4px', boxSizing: 'border-box'
                }}>{totalNotifications}</span>
              )}
              <div style={{
                width: '20px',
                height: '2px',
                backgroundColor: 'var(--text-strong)',
                transition: 'all 0.3s ease'
              }}></div>
              <div style={{
                width: '20px',
                height: '2px',
                backgroundColor: 'var(--text-strong)',
                transition: 'all 0.3s ease'
              }}></div>
              <div style={{
                width: '20px',
                height: '2px',
                backgroundColor: 'var(--text-strong)',
                transition: 'all 0.3s ease'
              }}></div>
            </button>

            <h1 style={{
              margin: '0',
              fontSize: isMobile ? '1.1rem' : '1.4rem',
              color: 'var(--text-strong)',
              fontWeight: '600',
              letterSpacing: '-0.5px'
            }}>
              {selectedCategory === 'user-dashboard'
                ? `${user.displayName}'s Dashboard`
                : selectedCategoryMeta?.name || 'Browse'}
            </h1>
          </div>

          {/* Global Search */}
          <div style={{ flex: isMobile ? '1 1 100%' : '1 1 0', maxWidth: isMobile ? 'none' : '520px', margin: isMobile ? '0' : '0 1.5rem', order: isMobile ? 3 : 0, position: 'relative' }}>
            <svg
              width="18" height="18" viewBox="0 0 24 24" fill="none"
              stroke="var(--text-soft)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
            >
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                const v = e.target.value;
                setSearchQuery(v);
                if (v.trim() && selectedCategory === 'user-dashboard') setSelectedCategory('all');
              }}
              placeholder="Search all of UTrade…"
              aria-label="Search listings"
              style={{
                width: '100%',
                padding: '0.6rem 2.4rem 0.6rem 2.6rem',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-full)',
                fontSize: isMobile ? '16px' : '0.9rem',
                fontFamily: 'var(--font)',
                color: 'var(--text-strong)',
                background: 'var(--surface-bg)',
                outline: 'none',
                transition: 'border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease'
              }}
              onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--maroon)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(136,28,28,0.1)'; e.currentTarget.style.background = 'var(--surface-card)'; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--border-subtle)'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.background = 'var(--surface-bg)'; }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                aria-label="Clear search"
                style={{
                  position: 'absolute', right: isMobile ? '6px' : '10px', top: '50%', transform: 'translateY(-50%)',
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  color: 'var(--text-soft)', fontSize: isMobile ? '1.4rem' : '1.1rem', lineHeight: 1,
                  padding: isMobile ? '8px 10px' : '4px'
                }}
              >
                ×
              </button>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {/* Home → All Listings */}
            <button
              onClick={() => { setSearchQuery(''); setSelectedCategory('all'); }}
              aria-label="Home — all listings"
              title="Home — all listings"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: '40px', height: '40px', flexShrink: 0,
                backgroundColor: 'transparent', color: 'var(--maroon)',
                border: '1px solid var(--maroon-border)', borderRadius: '10px',
                cursor: 'pointer', transition: 'background-color 0.2s ease, border-color 0.2s ease',
              }}
              onMouseOver={(e)=>{e.currentTarget.style.backgroundColor='var(--maroon-light)';e.currentTarget.style.borderColor='var(--maroon)';}}
              onMouseOut={(e)=>{e.currentTarget.style.backgroundColor='transparent';e.currentTarget.style.borderColor='var(--maroon-border)';}}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M3 9.5 12 3l9 6.5" /><path d="M5 10v10h14V10" />
              </svg>
            </button>

            {/* Help */}
            <button
              onClick={() => navigate('/help')}
              aria-label="Help center"
              title="Help center"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: '40px', height: '40px', flexShrink: 0,
                backgroundColor: 'transparent', color: 'var(--maroon)',
                border: '1px solid var(--maroon-border)', borderRadius: '10px',
                cursor: 'pointer', transition: 'background-color 0.2s ease, border-color 0.2s ease',
              }}
              onMouseOver={(e)=>{e.currentTarget.style.backgroundColor='var(--maroon-light)';e.currentTarget.style.borderColor='var(--maroon)';}}
              onMouseOut={(e)=>{e.currentTarget.style.backgroundColor='transparent';e.currentTarget.style.borderColor='var(--maroon-border)';}}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10" /><path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12" y2="17" />
              </svg>
            </button>

            {/* Your Profile Button */}
            <button
              onClick={() => setProfileModalOpen(true)}
              style={{
                display: isMobile ? 'none' : undefined,
                padding:'0.55rem 1.1rem',
                backgroundColor:'transparent',
                border:'1px solid var(--maroon-border)',
                borderRadius:'10px',
                cursor:'pointer',
                fontSize:'0.9rem',
                color:'var(--maroon)',
                fontWeight:'600',
                fontFamily:'var(--font-heading)',
                transition:'all 0.2s ease'
              }}
              onMouseOver={(e)=>{e.currentTarget.style.backgroundColor='var(--maroon-light)';e.currentTarget.style.borderColor='var(--maroon)';}}
              onMouseOut={(e)=>{e.currentTarget.style.backgroundColor='transparent';e.currentTarget.style.borderColor='var(--maroon-border)';}}
            >
              Profile
            </button>

            {/* Chat Drawer Toggle */}
            <button
              onClick={()=>setDrawerOpen(!drawerOpen)}
              style={{
                position:'relative',
                padding:'0.55rem 1.1rem',
                backgroundColor:'transparent',
                border:'1px solid var(--maroon-border)',
                borderRadius:'10px',
                cursor:'pointer',
                fontSize:'0.9rem',
                color:'var(--maroon)',
                fontWeight:'600',
                fontFamily:'var(--font-heading)',
                display: isMobile ? 'none' : 'flex',
                alignItems:'center',
                gap:'0.4rem',
                transition:'all 0.2s ease'
              }}
              onMouseOver={(e)=>{e.currentTarget.style.backgroundColor='var(--maroon-light)';e.currentTarget.style.borderColor='var(--maroon)';}}
              onMouseOut={(e)=>{e.currentTarget.style.backgroundColor='transparent';e.currentTarget.style.borderColor='var(--maroon-border)';}}
            >
              Chat
              {unreadTotal>0 && <span style={{position:'absolute', top:'-6px', right:'-6px', background:'var(--maroon)', color:'#fff', borderRadius:'50%', fontSize:'0.7rem', padding:'2px 5px'}}>{unreadTotal}</span>}
            </button>

            {/* Create Listing Button */}
            <button
              onClick={handleCreateListingClick}
              style={{
                padding: isMobile ? '0.55rem 1rem' : '0.6rem 1.4rem',
                whiteSpace: 'nowrap',
                backgroundColor: 'var(--maroon)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '10px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: '600',
                fontFamily: 'var(--font-heading)',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => {e.currentTarget.style.backgroundColor='var(--maroon-hover)';e.currentTarget.style.transform='translateY(-1px)';}}
              onMouseOut={(e) => {e.currentTarget.style.backgroundColor='var(--maroon)';e.currentTarget.style.transform='none';}}
            >
              {isMobile ? '+ Sell' : '+ Create Listing'}
            </button>

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              style={{
                display: isMobile ? 'none' : undefined,
                padding: '0.6rem 1.2rem',
                backgroundColor: 'transparent',
                color: 'var(--text-muted)',
                border: '1px solid var(--border-subtle)',
                borderRadius: '10px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontFamily: 'var(--font-heading)',
                fontWeight: '600',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--surface-panel)';e.currentTarget.style.borderColor='var(--text-soft)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';e.currentTarget.style.borderColor='var(--border-subtle)';
              }}
            >
              Logout
            </button>
          </div>
        </header>

        {/* Main Content Area */}
        <main style={{ padding: isMobile ? '1rem' : '2rem' }}>
          <div>
            {selectedCategory === 'user-dashboard' ? (
              // User Dashboard Section
              <div>
                {/* Admin-only Holds Panel */}
                {user && (user.email || '').toLowerCase() === 'hchhabra@umass.edu' && holds.length > 0 && (
                  <div style={{ marginBottom: '2rem', background:'#fff', borderRadius:'12px', boxShadow:'0 2px 4px rgba(0,0,0,0.1)', padding:'1rem' }}>
                    <h3 style={{ margin:'0 0 1rem 0', color:'var(--maroon)' }}>Holds Requiring Review</h3>
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(min(100%, 280px),1fr))', gap:'1rem' }}>
                      {holds.map(h => (
                        <div key={h._id} style={{ border:'1px solid #eee', borderRadius:'8px', padding:'0.75rem' }}>
                          <div style={{ fontWeight:700, color:'#333' }}>{h.title}</div>
                          <div style={{ fontSize:'0.85rem', color:'var(--text-soft)', margin:'0.25rem 0' }}>Item ID: {h.itemId}</div>
                          <div style={{ fontSize:'0.9rem', color:'var(--maroon)' }}>{h.holdReason}</div>
                          <div style={{ display:'flex', gap:'0.5rem', marginTop:'0.5rem' }}>
                            <button
                              onClick={async ()=>{
                                try {
                                  const res = await fetch(`${API_BASE}/holds/${h._id}/clear`, { method:'POST', credentials:'include' });
                                  if (res.ok) setHolds(prev=>prev.filter(x=>x._id!==h._id));
                                } catch (e) {}
                              }}
                              style={{ padding:'0.4rem 0.8rem', background:'var(--maroon)', color:'#fff', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'0.85rem' }}
                            >Clear Hold</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Dashboard Header */}
                <div style={{
                  marginBottom: '2.5rem',
                  paddingBottom: '1.5rem',
                  borderBottom: '1px solid var(--border-subtle)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem'
                }}>
                  <ProfilePicture
                    src={user.profilePicture}
                    alt={user.displayName || 'Student'}
                    size="large"
                    fallbackColor="var(--maroon)"
                  />
                  <div>
                    <h1 style={{ margin: '0 0 0.25rem 0', fontSize: 'var(--text-3xl)', letterSpacing: '-0.01em' }}>
                      Your Dashboard
                    </h1>
                    <p style={{ margin: '0', color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
                      Manage your listings and track your sales
                    </p>
                  </div>
                </div>

                {/* Active Listings */}
                <div className="section">
                  <h2 className="section-title">
                    Active Listings <span className="section-count">({userListings.current.length})</span>
                  </h2>
                  {userListings.current.length > 0 ? (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 300px), 1fr))',
                      gap: '1.5rem'
                    }}>
                      {userListings.current.map(listing => (
                        <ListingCard
                          key={listing._id}
                          listing={listing}
                          onDelete={handleDelete}
                          onEdit={handleEdit}
                          currentUser={user}
                          onChat={openChat}
                          onViewProfile={openSellerProfile}
                          isSaved={savedIds.has(listing._id)}
                          onToggleSave={handleToggleSave}
                        />
                      ))}
                    </div>
                  ) : (
                    <div style={{
                      textAlign: 'center',
                      padding: '2.5rem 2rem',
                      backgroundColor: 'var(--surface-panel)',
                      borderRadius: 'var(--radius-lg)',
                      border: '1px solid var(--border-subtle)'
                    }}>
                      <h3 style={{ margin: '0 0 0.5rem 0', fontSize: 'var(--text-xl)' }}>No active listings yet</h3>
                      <p style={{ margin: '0 0 1.25rem 0', color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
                        You have not posted anything yet. Start with a textbook, dorm item, or anything another student might need.
                      </p>
                      <button className="btn btn-primary btn-md" onClick={handleCreateListingClick}>
                        + Create Listing
                      </button>
                    </div>
                  )}
                </div>

                {/* Saved Listings — bookmarked items still on the market */}
                <div className="section">
                  <h2 className="section-title">
                    Saved Listings <span className="section-count">({savedListings.length})</span>
                  </h2>
                  {savedListings.length > 0 ? (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 300px), 1fr))',
                      gap: '1.5rem'
                    }}>
                      {savedListings.map(listing => (
                        <ListingCard
                          key={listing._id}
                          listing={listing}
                          onDelete={() => {}}
                          onEdit={null}
                          currentUser={user}
                          showActions={false}
                          onChat={openChat}
                          onViewProfile={openSellerProfile}
                          isSaved={savedIds.has(listing._id)}
                          onToggleSave={handleToggleSave}
                        />
                      ))}
                    </div>
                  ) : (
                    <div style={{
                      textAlign: 'center',
                      padding: '2.5rem 2rem',
                      backgroundColor: 'var(--surface-panel)',
                      borderRadius: 'var(--radius-lg)',
                      border: '1px solid var(--border-subtle)'
                    }}>
                      <h3 style={{ margin: '0 0 0.5rem 0', fontSize: 'var(--text-xl)' }}>No saved listings yet</h3>
                      <p style={{ margin: '0', color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
                        Tap the bookmark icon on any listing to save it here. Saved items disappear automatically once they’re bought.
                      </p>
                    </div>
                  )}
                </div>

                {/* Completed Sales */}
                {userListings.completed.length > 0 && (
                  <div className="section">
                    <h2 className="section-title">
                      Completed Sales <span className="section-count">({userListings.completed.length})</span>
                    </h2>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 300px), 1fr))',
                      gap: '1.5rem'
                    }}>
                      {userListings.completed.map(listing => (
                        <div key={listing._id} style={{ position: 'relative' }}>
                          <ListingCard
                            listing={listing}
                            onDelete={() => {}} // No delete for completed listings
                            onEdit={null} // No edit for completed listings
                            currentUser={user}
                            showActions={false} // Hide action buttons for completed listings
                            onViewProfile={openSellerProfile}
                          />
                          {/* Sold Badge */}
                          <div style={{
                            position: 'absolute',
                            top: '12px',
                            left: '12px',
                            padding: '0.25rem 0.75rem',
                            backgroundColor: '#28a745',
                            color: 'white',
                            borderRadius: '20px',
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem'
                          }}>
                            SOLD
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Stripe Transactions section removed in Phase 1 (ENABLE_STRIPE=false) */}



                {/* Item Requests */}
                <div className="section">
                  <h2 className="section-title">
                    Direct Item Requests <span className="section-count">({activeDirectRequests.length})</span>
                  </h2>

                  {activeDirectRequests.length > 0 && (
                    <div style={{
                      backgroundColor: 'var(--surface-card)',
                      border: '1px solid var(--border-subtle)',
                      borderRadius: 'var(--radius-lg)',
                      overflow: 'hidden'
                    }}>
                      {(() => {
                        const sortedDirect = activeDirectRequests.slice().sort((a,b)=> (a.userRole==='buyer' ? -1 : 1));
                        return sortedDirect.map((request, index) => {
                          const isFirstBuyer = request.userRole==='buyer' && !sortedDirect.slice(0,index).some(r=>r.userRole==='buyer');
                          const isFirstSeller = request.userRole==='seller' && !sortedDirect.slice(0,index).some(r=>r.userRole==='seller');
                          return (
                            <React.Fragment key={request._id}>
                              {isFirstBuyer && (
                                <div style={{ padding:'0.75rem 1rem', borderBottom:'1px solid #f1f3f4' }}>
                                  <h4 style={{ margin:0, color:'var(--maroon)', fontSize:'1rem' }}>Item Requests</h4>
                                </div>
                              )}
                              {isFirstSeller && (
                                <div style={{ padding:'0.75rem 1rem', borderBottom:'1px solid #f1f3f4' }}>
                                  <h4 style={{ margin:0, color:'var(--maroon)', fontSize:'1rem' }}>Sales Awaiting PIN Confirmation</h4>
                                </div>
                              )}
                              {/* Existing row markup */}
                              <div style={{
                                padding: '1rem',
                                borderBottom: index < sortedDirect.length - 1 ? '1px solid #f1f3f4' : 'none'
                              }}>
                                {/* Role indicator */}
                                <div style={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: '0.5rem',
                                  marginBottom: '0.75rem'
                                }}>
                                  <span style={{
                                    fontSize: '0.8rem',
                                    fontWeight: '600',
                                    color: '#495057'
                                  }}>
                                    {`You're ${request.userRole === 'seller' ? 'Selling' : 'Buying'}`}
                                  </span>
                                  <strong style={{ color: '#212529', fontSize: '0.95rem' }}>
                                    {request.listing?.title || 'Listing not found'}
                                  </strong>
                                  <span style={{
                                    backgroundColor: 
                                      request.status === 'completed' ? '#d4edda' : '#fff8e1',
                                    color: 
                                      request.status === 'completed' ? '#155724' : '#856404',
                                    fontSize: '0.9rem',
                                    fontWeight: '600',
                                    padding: '0.15rem 0.4rem',
                                    borderRadius: '8px',
                                    textTransform: 'none'
                                  }}>
                                    {request.status === 'pending' ? (request.userRole === 'seller' ? 'Waiting for your approval' : 'Waiting for seller approval') : request.status}
                                  </span>
                                  
                                  {/* Dismiss button for declined (buyer) and expired (either party) requests */}
                                  {((request.status === 'declined' && request.userRole === 'buyer') || request.status === 'expired') && (
                                    <button
                                      onClick={() => dismissBuyingRequest(request._id)}
                                      style={{
                                        marginLeft: 'auto',
                                        padding: '0.2rem 0.4rem',
                                        backgroundColor: 'rgba(0,0,0,0.1)',
                                        border: 'none',
                                        cursor: 'pointer',
                                        fontSize: '1rem',
                                        lineHeight: '1',
                                        color: 'var(--text-soft)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '24px',
                                        height: '24px',
                                        borderRadius: '50%',
                                        transition:'background-color .2s'
                                      }}
                                      onMouseOver={(e)=>e.currentTarget.style.backgroundColor='rgba(0,0,0,0.2)'}
                                      onMouseOut={(e)=>e.currentTarget.style.backgroundColor='rgba(0,0,0,0.1)'}
                                      title="Dismiss notification"
                                    >
                                      ×
                                    </button>
                                  )}
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
                                  <div style={{ flex: 1 }}>
                                    {/* Other party info */}
                                    <div style={{ marginBottom: '0.5rem', fontSize: '0.9rem', color: '#495057' }}>
                                      <strong>
                                        {request.userRole === 'seller' ? 'Buyer:' : 'Seller:'}
                                      </strong> {
                                        request.userRole === 'seller' 
                                          ? (request.buyer?.displayName || request.buyer?.name || 'Unknown')
                                          : (request.seller?.displayName || request.seller?.name || 'Unknown')
                                      }
                                    </div>

                                    {/* Message */}
                                    {request.message && (
                                      <div style={{ 
                                        marginBottom: '0.5rem', 
                                        fontSize: '0.85rem', 
                                        color: 'var(--text-soft)',
                                        fontStyle: 'italic',
                                        backgroundColor: '#f0efeb',
                                        padding: '0.5rem',
                                        borderRadius: '6px',
                                        borderLeft: '3px solid #dee2e6'
                                      }}>
                                        "{request.message}"
                                      </div>
                                    )}

                                    {/* Expiry info */}
                                    <div style={{ fontSize: '0.8rem', color: 'var(--text-soft)', marginBottom: '0.5rem' }}>
                                      {request.status === 'pending' 
                                        ? `Expires: ${new Date(request.expiresAt).toLocaleDateString('en-US', {
                                            month: 'short',
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                          })}`
                                        : `Created: ${new Date(request.createdAt).toLocaleDateString('en-US', {
                                            month: 'short',
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                          })}`
                                      }
                                    </div>

                                    {/* PIN Input for Sellers (Approved requests) */}
                                    {request.status === 'approved' && request.userRole === 'seller' && (
                                      <div style={{
                                        marginTop: '0.5rem',
                                        padding: '0.5rem',
                                        backgroundColor: '#fff8e1',
                                        borderRadius: '6px',
                                        border: '1px solid #ffecb3'
                                      }}>
                                        <div style={{ fontSize: '0.8rem', color: '#7c4700', marginBottom: '0.5rem', fontWeight: 600 }}>
                                          <strong>PIN Generated:</strong> When the buyer shows you their PIN, enter it below to complete the transaction.
                                        </div>
                                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                          <input
                                            type="text"
                                            placeholder="Enter 6-digit PIN"
                                            maxLength={6}
                                            style={{
                                              padding: '0.4rem',
                                              border: '1px solid #ced4da',
                                              borderRadius: '4px',
                                              fontSize: '0.9rem',
                                              width: '120px'
                                            }}
                                            onChange={(e) => {
                                              const pin = e.target.value;
                                              e.target.dataset.pin = pin;
                                            }}
                                          />
                                          <button
                                            onClick={(event) => {
                                              const input = event.target.previousElementSibling;
                                              const pin = input.dataset.pin || input.value;
                                              confirmBuyingRequestPin(request._id, pin);
                                            }}
                                            style={{
                                              padding: '0.4rem 0.8rem',
                                              backgroundColor: 'var(--maroon)',
                                              color: 'white',
                                              border: 'none',
                                              borderRadius: '4px',
                                              cursor: 'pointer',
                                              fontSize: '0.8rem',
                                              fontWeight: '600'
                                            }}
                                          >
                                            Confirm
                                          </button>
                                        </div>
                                      </div>
                                    )}

                                    {/* Declined message for buyers (soft amber, not alarming red) */}
                                    {request.status === 'declined' && request.userRole === 'buyer' && (
                                      <div style={{
                                        marginTop: '0.5rem',
                                        padding: '0.75rem',
                                        backgroundColor: '#fff8e1',
                                        borderRadius: '6px',
                                        border: '1px solid #ffecb3'
                                      }}>
                                        <div style={{ fontSize: '0.85rem', color: '#7c4700', fontWeight: '600' }}>
                                          Request not accepted
                                        </div>
                                        <div style={{ fontSize: '0.8rem', color: '#7c4700', marginTop: '0.25rem' }}>
                                          The seller didn’t accept this request. No worries — you can message other sellers or browse similar items.
                                        </div>
                                      </div>
                                    )}

                                    {/* Expired message (either party) */}
                                    {request.status === 'expired' && (
                                      <div style={{
                                        marginTop: '0.5rem',
                                        padding: '0.75rem',
                                        backgroundColor: '#f0efeb',
                                        borderRadius: '6px',
                                        border: '1px solid #dee2e6'
                                      }}>
                                        <div style={{ fontSize: '0.85rem', color: '#495057', fontWeight: '600' }}>
                                          Transaction expired
                                        </div>
                                        <div style={{ fontSize: '0.8rem', color: 'var(--text-soft)', marginTop: '0.25rem' }}>
                                          {request.userRole === 'seller'
                                            ? 'This reservation expired, so your listing is public again — no action needed. You can edit it or just wait for new requests.'
                                            : 'This reservation expired and the item is available again. You can request it again or browse other listings.'}
                                        </div>
                                        <div style={{ fontSize: '0.78rem', color: 'var(--text-soft)', marginTop: '0.4rem' }}>
                                          Dismiss it with the × above.
                                        </div>
                                      </div>
                                    )}

                                    {/* Completed message */}
                                    {request.status === 'completed' && (
                                      <div style={{
                                        marginTop: '0.5rem',
                                        padding: '0.75rem',
                                        backgroundColor: '#d1ecf1',
                                        borderRadius: '6px',
                                        border: '1px solid #bee5eb'
                                      }}>
                                        <div style={{ fontSize: '0.85rem', color: '#0c5460', fontWeight: '600' }}>
                                          Transaction Completed
                                        </div>
                                        <div style={{ fontSize: '0.8rem', color: '#0c5460', marginTop: '0.25rem' }}>
                                          {request.userRole === 'seller' 
                                            ? 'You have successfully sold this item!'
                                            : 'You have successfully purchased this item!'
                                          }
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  
                                  {request.status === 'approved' && request.userRole === 'buyer' && request.pinRaw && (
                                    <span style={{ background:'#fff8e1', color:'var(--maroon)', padding:'3px 10px', borderRadius:'6px', border:'1px solid #ffecb3', fontWeight:700, letterSpacing:'1px', fontSize:'0.95rem' }}>
                                      PIN {request.pinRaw}
                                    </span>
                                  )}
                                  
                                  {/* Action buttons for sellers with pending requests */}
                                  {request.status === 'pending' && request.userRole === 'seller' && (
                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                      <button
                                        onClick={() => handleApproveBuyingRequest(request._id)}
                                        style={{
                                          padding: '0.5rem 1rem',
                                          backgroundColor: 'var(--maroon)',
                                          color: 'white',
                                          border: 'none',
                                          borderRadius: '6px',
                                          cursor: 'pointer',
                                          fontSize: '0.8rem',
                                          fontWeight: '600'
                                        }}
                                      >
                                        Approve
                                      </button>
                                      <button
                                        onClick={() => handleDeclineBuyingRequest(request._id)}
                                        style={{
                                          padding: '0.5rem 1rem',
                                          backgroundColor: 'transparent',
                                          color: 'var(--text-soft)',
                                          border: '1px solid #ced4da',
                                          borderRadius: '6px',
                                          cursor: 'pointer',
                                          fontSize: '0.8rem',
                                          fontWeight: '600'
                                        }}
                                        onMouseOver={(e)=>{e.target.style.backgroundColor='#f0efeb';}}
                                        onMouseOut={(e)=>{e.target.style.backgroundColor='transparent';}}
                                      >
                                        Decline
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </React.Fragment>
                          );
                        });
                      })()}
                    </div>
                  )}

                  

                  {(activeDirectRequests.length === 0 && visibleCompletedDirectTransactions.length === 0) && (
                    <div style={{
                      textAlign: 'center',
                      padding: '2.5rem 2rem',
                      backgroundColor: 'var(--surface-panel)',
                      border: '1px solid var(--border-subtle)',
                      borderRadius: 'var(--radius-lg)',
                      color: 'var(--text-muted)'
                    }}>
                      <p style={{ margin: '0', fontSize: 'var(--text-sm)' }}>
                        No active direct transactions at the moment. Completed transactions will still show below.
                      </p>
                    </div>
                  )}
                </div>

                {/* Transaction Completion Notifications */}
                {/* {transactionNotifications.length > 0 && (
                  <div style={{ marginBottom: '2rem' }}>
                    <h3 style={{ margin: '0 0 1rem 0', color: 'var(--maroon)', fontSize: '1.2rem' }}>
                      Transaction Notifications
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {transactionNotifications.map((notification) => (
                        <div
                          key={notification.id}
                          style={{
                            padding: '1rem',
                            backgroundColor: '#d4edda',
                            borderRadius: '8px',
                            border: '1px solid #c3e6cb',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <div>
                              <div style={{ fontWeight: '600', color: '#155724' }}>
                                {notification.message}
                              </div>
                              <div style={{ fontSize: '0.8rem', color: 'var(--text-soft)' }}>
                                {notification.timestamp.toLocaleTimeString()}
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => dismissTransactionNotification(notification.id)}
                            style={{
                              backgroundColor: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: '1.2rem',
                              color: 'var(--text-soft)',
                              padding: '0.25rem',
                              borderRadius: '50%',
                              width: '28px',
                              height: '28px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            title="Dismiss notification"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )} */}

                {/* Transaction History */}
                <TransactionHistory transactions={transactionHistory} />

                {/* Stripe payment setup removed in Phase 1 (ENABLE_STRIPE=false) */}
              </div>
            ) : (
              // Marketplace Categories Section
              <div>
                {/* Category Header */}
                <div style={{
                  marginBottom: '2.5rem',
                  paddingBottom: '1.5rem',
                  borderBottom: '1px solid var(--border-subtle)'
                }}>
                  <h1 style={{ margin: '0 0 0.25rem 0', fontSize: 'var(--text-3xl)', letterSpacing: '-0.01em' }}>
                    {searchQuery.trim()
                      ? `Results for "${searchQuery.trim()}"`
                      : (selectedCategoryMeta?.name || 'Browse Marketplace')}
                  </h1>
                  <p style={{ margin: '0', color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
                    {filteredListings.filter(listing =>
                      listing.ownerId &&
                      (typeof listing.ownerId === 'object' ? listing.ownerId._id : listing.ownerId) !== user._id
                    ).length} items available from other students
                  </p>
                </div>

                {/* Listings Grid */}
                {filteredListings.length > 0 ? (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 300px), 1fr))',
                    gap: '1.5rem'
                  }}>
                    {filteredListings.map(listing => (
                      <ListingCard
                        key={listing._id}
                        listing={listing}
                        onDelete={handleDelete}
                        onEdit={handleEdit}
                        currentUser={user}
                        onChat={openChat}
                        onViewProfile={openSellerProfile}
                        isSaved={savedIds.has(listing._id)}
                        onToggleSave={handleToggleSave}
                      />
                    ))}
                  </div>
                ) : (
                  <div style={{
                    textAlign: 'center',
                    padding: '3.5rem 2rem',
                    backgroundColor: 'var(--surface-panel)',
                    border: '1px solid var(--border-subtle)',
                    borderRadius: 'var(--radius-lg)'
                  }}>
                    <h3 style={{ margin: '0 0 0.5rem 0', fontSize: 'var(--text-xl)' }}>No items found</h3>
                    <p style={{ margin: '0', color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
                      {searchQuery.trim()
                        ? `No listings match "${searchQuery.trim()}". Try a different search or browse All Listings.`
                        : selectedCategory === 'all'
                          ? 'No listings yet. Be the first to post an item for the UMass community.'
                          : 'No items in this category yet. Try All Listings or create a listing.'}
                    </p>
                    <div style={{ marginTop: '1.25rem', display: 'flex', justifyContent: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                      {(selectedCategory !== 'all' || searchQuery.trim()) && (
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => { setSearchQuery(''); handleCategoryClick('all'); }}
                        >
                          Browse All Listings
                        </button>
                      )}
                      <button className="btn btn-primary btn-sm" onClick={handleCreateListingClick}>
                        + Create Listing
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Listing Form Modal */}
      {showListingForm && (
        <div style={{
          position: 'fixed',
          top: '0',
          left: '0',
          right: '0',
          bottom: '0',
          backgroundColor: 'rgba(0,0,0,0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 3000,
          padding: isMobile ? '0' : '2rem',
          animation: 'fadeIn 0.2s ease-out'
        }}>
          <style>{`@keyframes fadeIn{from{opacity:0}to{opacity:1}}@keyframes slideUp{from{transform:translateY(30px);opacity:0}to{transform:translateY(0);opacity:1}}`}</style>
          <div style={{
            backgroundColor: 'white',
            borderRadius: isMobile ? '0' : '16px',
            padding: isMobile ? '1.25rem' : '2rem',
            maxWidth: isMobile ? '100%' : '600px',
            width: '100%',
            height: isMobile ? '100%' : undefined,
            maxHeight: isMobile ? '100%' : '90vh',
            overflow: 'auto',
            position: 'relative',
            animation: 'slideUp 0.3s ease-out'
          }}>
            {/* Close (×) Button */}
            <button
              onClick={() => {
                setShowListingForm(false);
                setEditingListing(null);
              }}
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: 'rgba(0,0,0,0.1)',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                cursor: 'pointer',
                fontSize: '1.5rem',
                lineHeight: '1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background-color 0.2s ease'
              }}
              onMouseOver={(e) => (e.currentTarget.style.background = 'rgba(0,0,0,0.2)')}
              onMouseOut={(e) => (e.currentTarget.style.background = 'rgba(0,0,0,0.1)')}
            >
              ×
            </button>
            <h2 style={{
              margin: '0 0 1.5rem 0',
              color: 'var(--maroon)',
              textAlign: 'center'
            }}>
              {editingListing ? 'Edit Listing' : 'Create New Listing'}
            </h2>
            <ListingForm
              initialData={editingListing}
              onSubmit={handleListingSubmit}
              onCancel={() => {
                setShowListingForm(false);
                setEditingListing(null);
              }}
              user={user}
              onPaymentSetup={() => {
                setShowListingForm(false);
                setEditingListing(null);
              }}
            />
          </div>
        </div>
      )}

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div
          style={{
            position: 'fixed',
            top: '0',
            left: '0',
            right: '0',
            bottom: '0',
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 999
          }}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Chat Modal */}
      {chatModalOpen && (
        <ChatModal
          room={chatRoom}
          currentUser={user}
          onClose={() => {
            setChatModalOpen(false);
            setChatRoom(null);
          }}
        />
      )}

      {/* Chat Drawer */}
      {drawerOpen && (
        <>
          {isMobile && (
            <div
              onClick={() => setDrawerOpen(false)}
              aria-hidden="true"
              style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(32,32,32,0.45)', zIndex: 2400 }}
            />
          )}
          <ChatDrawer
            onSelect={(room)=>{setDrawerOpen(false); setChatRoom(room); setChatModalOpen(true); openRoom(room);}}
            onClose={()=>setDrawerOpen(false)}
          />
        </>
      )}

      {/* User Profile Modal */}
      <UserProfile
        isOpen={profileModalOpen}
        onClose={() => setProfileModalOpen(false)}
        user={user}
        onProfileUpdate={handleProfileUpdate}
      />

      {/* Seller Profile Modal */}
      <UserProfile
        isOpen={sellerProfileModalOpen}
        onClose={() => {
          setSellerProfileModalOpen(false);
          setSelectedSellerId(null);
        }}
        user={user}
        targetUserId={selectedSellerId}
      />

      {/* PaymentSetupModal removed in Phase 1 (ENABLE_STRIPE=false) */}

      {/* Toasts are rendered globally by ToastContext — Snackbar removed */}

      <ConfirmModal
        open={confirmModal.open}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmLabel={confirmModal.confirmLabel || 'Confirm'}
        onConfirm={() => { confirmModal.onConfirm && confirmModal.onConfirm(); setConfirmModal(m => ({ ...m, open: false })); }}
        onCancel={() => setConfirmModal(m => ({ ...m, open: false }))}
        danger={confirmModal.danger}
      />
    </div>
    </>
  );
}

export default Dashboard; 